import { useEffect, useState } from "react";
import api, { brl } from "../lib/api";
import {
  ShoppingCart, Package, Wrench, DollarSign, TrendingUp, AlertTriangle,
  Clock, MessageCircle,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { filterOverdue, buildStorageChargeMsg } from "../lib/osStorage";

function KPI({ icon: Icon, label, value, hint, color = "#2563EB", testId }) {
  return (
    <div className="bg-white border border-slate-200 p-5 rounded-sm hover-lift" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">{label}</span>
        <div className="w-8 h-8 rounded-sm flex items-center justify-center"
             style={{ backgroundColor: `${color}15`, color }}>
          <Icon size={16} />
        </div>
      </div>
      <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [fin, setFin] = useState(null);
  const [overdue, setOverdue] = useState([]);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data));
    api.get("/financial/summary").then((r) => setFin(r.data));
    api.get("/service-orders").then((r) => setOverdue(filterOverdue(r.data || [])));
  }, []);

  const totalFee = overdue.reduce((s, x) => s + x.info.fee, 0);

  const chargeStorage = (so, info) => {
    const clean = String(so.customer_phone || "").replace(/\D/g, "");
    const num = clean.length === 11 || clean.length === 10 ? `55${clean}` : clean;
    if (!num) return alert("Cliente sem telefone cadastrado.");
    const text = buildStorageChargeMsg(so, info);
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (!kpis) return <div className="text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">Painel de Controle</div>
          <h1 className="font-display text-4xl font-bold text-slate-900">Dashboard</h1>
        </div>
        <div className="text-sm text-slate-500">
          {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={ShoppingCart} label="Vendas Hoje" value={kpis.today_sales_count}
             hint={brl(kpis.today_sales_total)} testId="kpi-today-sales" />
        <KPI icon={DollarSign} label="Caixa" value={brl(kpis.caixa)}
             hint={`Receita ${brl(kpis.receita_total)}`} color="#10B981" testId="kpi-caixa" />
        <KPI icon={Package} label="Estoque Crítico" value={kpis.low_stock_count}
             hint={`${kpis.total_products} produtos`} color="#F59E0B" testId="kpi-low-stock" />
        <KPI icon={Wrench} label="OS em Aberto" value={kpis.open_service_orders}
             hint="Ordens de serviço ativas" color="#2563EB" testId="kpi-open-os" />
      </div>

      {/* Overdue OS storage fee alert */}
      {overdue.length > 0 && (
        <div
          className="bg-gradient-to-br from-red-50 to-amber-50 border-l-4 border-red-500 rounded-sm p-5"
          data-testid="overdue-storage-card"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-sm bg-red-500 text-white flex items-center justify-center flex-shrink-0">
                <Clock size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-1">
                  Aparelhos parados — Taxa de armazenamento
                </div>
                <h3 className="font-display font-bold text-2xl text-slate-900">
                  <span data-testid="overdue-count">{overdue.length}</span>{" "}
                  {overdue.length === 1 ? "aparelho parado" : "aparelhos parados"} há mais de 3 dias
                </h3>
                <div className="text-sm text-slate-600 mt-1">
                  Taxa acumulada total:{" "}
                  <span className="font-bold text-red-600 text-lg" data-testid="overdue-total-fee">
                    {brl(totalFee)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 grid gap-2">
            {overdue.slice(0, 6).map(({ so, info }) => (
              <div
                key={so.id}
                data-testid={`overdue-row-${so.id}`}
                className="bg-white rounded-sm border border-red-200 p-3 flex items-center justify-between gap-3 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">
                    {so.customer_name || "Cliente sem nome"}{" "}
                    <span className="text-slate-500 font-normal text-sm">
                      · {so.brand ? so.brand + " " : ""}{so.device}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {info.daysStored} dias armazenado ·{" "}
                    <span className="font-semibold text-red-600">
                      {info.daysOverdue} dia{info.daysOverdue !== 1 && "s"} de taxa
                    </span>
                    {" · "}
                    <span className="font-bold text-red-600">{brl(info.fee)}</span>
                    {so.price > 0 && (
                      <>
                        {" + serviço "}{brl(so.price)}{" = "}
                        <span className="font-bold text-slate-900">
                          {brl(so.price + info.fee)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => chargeStorage(so, info)}
                  data-testid={`overdue-wa-${so.id}`}
                  disabled={!so.customer_phone}
                  title={
                    so.customer_phone
                      ? "Enviar cobrança pelo WhatsApp"
                      : "Cliente sem telefone cadastrado"
                  }
                  className="flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <MessageCircle size={13} /> Cobrar WhatsApp
                </button>
              </div>
            ))}
            {overdue.length > 6 && (
              <div className="text-xs text-slate-500 text-center pt-1">
                + {overdue.length - 6} outras OS atrasadas. Veja todas em{" "}
                <a href="/app/service-orders" className="text-[#2563EB] font-semibold">
                  Ordens de Serviço
                </a>
                .
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg">Fluxo Financeiro (6m)</h3>
            <TrendingUp size={16} className="text-[#2563EB]" />
          </div>
          <div style={{ width: "100%", height: 256 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={fin?.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip />
                <Line type="monotone" dataKey="receita" stroke="#10B981" strokeWidth={2} />
                <Line type="monotone" dataKey="despesa" stroke="#EF4444" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={16} className="text-amber-500" />
            <h3 className="font-display font-bold text-lg">Estoque Baixo</h3>
          </div>
          {kpis.low_stock_items.length === 0 ? (
            <div className="text-sm text-slate-500">Tudo certo por aqui ✓</div>
          ) : (
            <ul className="space-y-2">
              {kpis.low_stock_items.map((p) => (
                <li key={p.id} className="flex justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <span className="text-slate-700">{p.name}</span>
                  <span className="font-semibold text-amber-600">{p.stock} un</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-display font-bold text-lg mb-3">Últimas Vendas</h3>
          {kpis.recent_sales.length === 0 ? (
            <div className="text-sm text-slate-500">Nenhuma venda ainda.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {kpis.recent_sales.map((s) => (
                <li key={s.id} className="flex justify-between py-2 border-b border-slate-100 last:border-0">
                  <span>{s.customer_name || "Cliente não informado"}</span>
                  <span className="font-semibold">{brl(s.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-display font-bold text-lg mb-3">Ordens Recentes</h3>
          {kpis.recent_service_orders.length === 0 ? (
            <div className="text-sm text-slate-500">Nenhuma ordem ainda.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {kpis.recent_service_orders.map((o) => (
                <li key={o.id} className="flex justify-between py-2 border-b border-slate-100 last:border-0">
                  <span>{o.customer_name} — {o.device}</span>
                  <span className="text-xs uppercase tracking-wider px-2 py-0.5 bg-slate-100 text-slate-700 rounded-sm">
                    {o.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
